/**
 * Event-Overlay über dem Bedarfsplaner-Monatsgrid (Phase 12 von #763, siehe BEDARFSPLANER.md §3).
 * Events werden als absolut positionierte Balken/Karten ÜBER `.month` gezeichnet (Google-Calendar-Stil),
 * nie in eine Tageszelle eingebettet. Mehrtägige Events → schlichter Balken (.mevent-bar) mit Gäste- und
 * Deckungs-Badge (die Bedarfe pro Tag zeigen zusätzlich die In-Zelle-Gruppen, siehe bedarfChip.js);
 * eintägige Events → aufgeklappte Karte (.expanded) mit einer .evrow je Bedarfszeile.
 *
 * Layout: pro Event eine Lane (niedrigster Index ohne datumsüberlappenden früheren Event), Lane-Höhen
 * summiert; jede abgedeckte Zelle bekommt einen .ev-spacer, damit die Bedarf-Chips nicht unter den
 * schwebenden Balken landen. Positionen werden aus den echten Zell-Rects gemessen (dadurch ist die
 * linke KW-Spalte automatisch berücksichtigt) und per ResizeObserver bei Größenänderung neu berechnet.
 * Mehrwöchige Events werden in ein Segment je Wochenzeile gesplittet.
 *
 * In der Zelle gehört alles Event-Bezogene in den `.mcell-events`-Block direkt unter den Balken (erst die
 * In-Zelle-Gruppen mehrtägiger Events in Lane-Reihenfolge, dann eintägige Karten), erst danach folgen die
 * normalen Bereich-Gruppen — so steht nichts zwischen einem Event-Balken und seinen Schichten.
 *
 * Auf-/Zuklappen: Chevron im Balken bzw. im Kartenkopf togglet `.collapsed` auf allen Elementen mit der
 * data-event-id (Balken-Segmente, Karten, In-Zelle-Gruppen) — ohne Re-Render, danach Neupositionierung.
 * Die In-Zelle-Gruppen haben zusätzlich einen eigenen Tages-Chevron (bedarfChip.js), der nur sie betrifft.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { stateCls, fuelleZaehler } from '../util/bedarfState.js';
import { faerbeEventElement } from '../util/eventFarben.js';

let resizeObserver = null;
let positioniereAktuell = null;
// Kontext des zuletzt gerenderten Overlays für das Auf-/Zuklappen (auch aus den In-Zelle-Gruppen).
let klappKontext = null;

// Abstand zwischen gestapelten Balken (Lanes) mehrtägiger Events in px.
const LANE_ABSTAND = 5;
// Abstand zwischen dem untersten Balken und dem Zell-Inhalt darunter (Event-Gruppen) in px — groß genug,
// dass sich die Hover-Umrandungen von Balken und Gruppe nicht berühren.
const INHALT_ABSTAND = 8;

/** Kurzformat einer Uhrzeit: volle Stunde ohne Minuten (18), sonst mit (18:30). */
function kurzZeit(datum) {
	const d = dayjs(datum);
	return d.minute() === 0 ? d.format('H') : d.format('H:mm');
}

/** Summiert filled/need über die Bedarfszeilen eines Events (Deckungs-Badge). */
function gesamtDeckung(rows) {
	return rows.reduce((acc, r) => ({
		filled: acc.filled + (r.filled || 0),
		need: acc.need + r.BedarfAnzahl,
		geplant: acc.geplant + (r.geplant || 0),
	}), { filled: 0, need: 0, geplant: 0 });
}

function istEintaegig(ev) {
	return dayjs(ev.DatumVon).isSame(dayjs(ev.DatumBis), 'day');
}

// Höhe eines mehrtägigen Balkens in px — muss zu `.mevent-bar:not(.expanded) { height }` im CSS passen.
const BALKEN_HOEHE = 24;

/** Höhe eines Events: schlichter Balken BALKEN_HOEHE, aufgeklappte Karte 26 + Zeilen*19 + 6. */
function eventHoehe(ev, rows) {
	return istEintaegig(ev) ? 26 + rows.length * 19 + 6 : BALKEN_HOEHE;
}

/**
 * Lane je Event: niedrigster Index, der nicht von einem früher (nach Startdatum) einsortierten,
 * datumsüberlappenden Event belegt ist.
 * @returns {Map<string, number>} EventID -> Lane
 */
function berechneLanes(events) {
	const sortiert = [...events].sort((a, b) => new Date(a.DatumVon) - new Date(b.DatumVon));
	const laneById = new Map();
	const platziert = [];
	sortiert.forEach((ev) => {
		const von = dayjs(ev.DatumVon).startOf('day');
		const bis = dayjs(ev.DatumBis).startOf('day');
		let lane = 0;
		const ueberlappt = (p) => p.lane === lane && !von.isAfter(p.bis, 'day') && !bis.isBefore(p.von, 'day');
		while (platziert.some(ueberlappt)) {
			lane += 1;
		}
		laneById.set(ev._id, lane);
		platziert.push({ von, bis, lane });
	});
	return laneById;
}

function setzeToggleZustand(toggle, eingeklappt) {
	toggle.title = eingeklappt ? 'Schichten einblenden' : 'Schichten ausblenden';
	toggle.innerHTML = `<i class="bi ${eingeklappt ? 'bi-chevron-right' : 'bi-chevron-down'}"></i>`;
}

/**
 * Chevron zum Auf-/Zuklappen eines Events (Balken, Kartenkopf, In-Zelle-Gruppe); der Klick öffnet NICHT
 * den Event-Editor. Wirkt über den Kontext des zuletzt gerenderten Overlays (siehe rendereEventOverlay).
 * @param {string} eventID
 * @param {boolean} eingeklappt Anfangszustand
 */
function baueToggle(eventID, eingeklappt) {
	const toggle = document.createElement('span');
	toggle.className = 'evtoggle';
	setzeToggleZustand(toggle, eingeklappt);
	toggle.addEventListener('click', (e) => {
		e.stopPropagation();
		klappeEventUm(eventID);
	});
	return toggle;
}

/**
 * Klappt ein Event im DOM um: `.collapsed` auf allen Elementen des Events, Chevrons umstellen, Zustand
 * beim Aufrufer merken und die Balken neu positionieren (Zeilenhöhen ändern sich).
 */
function klappeEventUm(eventID) {
	if (!klappKontext) return;
	setzeEventKlappzustand(eventID, !klappKontext.istEingeklappt(eventID));
	if (positioniereAktuell) positioniereAktuell();
}

/** Setzt den Klappzustand eines Events im DOM (ohne Neupositionierung) und meldet ihn dem Aufrufer. */
function setzeEventKlappzustand(eventID, eingeklappt) {
	const { month, onKlappzustand } = klappKontext;
	onKlappzustand(eventID, eingeklappt);
	month.querySelectorAll(`[data-event-id="${eventID}"]`).forEach((el) => {
		el.classList.toggle('collapsed', eingeklappt);
		// Nur die Event-Chevrons umstellen — die Tages-Chevrons der In-Zelle-Gruppen haben eigenen Zustand.
		el.querySelectorAll('.evtoggle:not(.tagtoggle)').forEach((toggle) => setzeToggleZustand(toggle, eingeklappt));
	});
}

/**
 * Klappt alle Events des Overlays auf einmal zu bzw. auf (globaler Bereich/Posten-Umschalter), ohne Re-Render.
 * @param {boolean} eingeklappt
 */
function setzeAlleEventsEingeklappt(eingeklappt) {
	if (!klappKontext) return;
	const eventIDs = new Set(Array.from(klappKontext.month.querySelectorAll('[data-event-id]'), (el) => el.dataset.eventId));
	eventIDs.forEach((eventID) => setzeEventKlappzustand(eventID, eingeklappt));
	if (positioniereAktuell) positioniereAktuell();
}

/**
 * Hover-Verknüpfung: Maus über einem Element eines Events hebt alle Elemente dieses Events hervor
 * (Balken-Segmente, Karte, In-Zelle-Gruppen), damit klar ist, was zusammengehört.
 */
function verknuepfeHover(month) {
	month.querySelectorAll('[data-event-id]').forEach((el) => {
		const alle = () => month.querySelectorAll(`[data-event-id="${el.dataset.eventId}"]`);
		el.addEventListener('mouseenter', () => alle().forEach((x) => x.classList.add('hl')));
		el.addEventListener('mouseleave', () => alle().forEach((x) => x.classList.remove('hl')));
	});
}

/** Schlichter Balken für ein mehrtägiges Event. */
function baueBar(ev, rows, onEventKlick, eingeklappt) {
	const bar = document.createElement('div');
	bar.className = `mevent-bar${eingeklappt ? ' collapsed' : ''}`;
	bar.dataset.eventId = ev._id;
	faerbeEventElement(bar, ev);
	if (rows.length > 0) bar.appendChild(baueToggle(ev._id, eingeklappt));
	const icon = document.createElement('i');
	icon.className = `bi bi-${ev.Icon || 'stars'}`;
	bar.appendChild(icon);
	bar.appendChild(document.createTextNode(ev.Titel || ''));
	const sp = document.createElement('span');
	sp.className = 'sp';
	bar.appendChild(sp);
	if (ev.Gaestezahl) {
		const gst = document.createElement('span');
		gst.className = 'gst';
		gst.innerHTML = '<i class="bi bi-people-fill"></i>';
		gst.appendChild(document.createTextNode(ev.Gaestezahl));
		bar.appendChild(gst);
	}
	const { filled, need, geplant } = gesamtDeckung(rows);
	if (need > 0) {
		const cnt = document.createElement('span');
		cnt.className = 'cnt';
		// Besetzung bzw. im Kosten-Modus die geplanten AG-Kosten des Events (wie bei den Bedarfschips)
		fuelleZaehler(cnt, filled, need, geplant);
		bar.appendChild(cnt);
	}
	bar.addEventListener('click', () => onEventKlick(ev));
	return bar;
}

/** Aufgeklappte Karte für ein eintägiges Event — je Bedarfszeile eine klickbare .evrow. */
function baueExpandedCard(ev, rows, onEventKlick, onEvrowKlick, eingeklappt) {
	const bar = document.createElement('div');
	bar.className = `mevent-bar expanded${eingeklappt ? ' collapsed' : ''}`;
	bar.dataset.eventId = ev._id;
	faerbeEventElement(bar, ev);

	const evh = document.createElement('div');
	evh.className = 'evh';
	if (rows.length > 0) evh.appendChild(baueToggle(ev._id, eingeklappt));
	const icon = document.createElement('i');
	icon.className = `bi bi-${ev.Icon || 'stars'}`;
	evh.appendChild(icon);
	const ttx = document.createElement('span');
	ttx.className = 'ttx';
	ttx.textContent = ev.Titel || '';
	evh.appendChild(ttx);
	if (ev.Gaestezahl) {
		const gst = document.createElement('span');
		gst.className = 'gst';
		gst.innerHTML = '<i class="bi bi-people-fill"></i>';
		gst.appendChild(document.createTextNode(ev.Gaestezahl));
		evh.appendChild(gst);
	}
	const { filled, need, geplant } = gesamtDeckung(rows);
	if (need > 0) {
		const cnt = document.createElement('span');
		cnt.className = 'cnt';
		// Besetzung bzw. im Kosten-Modus die geplanten AG-Kosten des Events (wie bei den Bedarfschips)
		fuelleZaehler(cnt, filled, need, geplant);
		evh.appendChild(cnt);
	}
	evh.addEventListener('click', () => onEventKlick(ev));
	bar.appendChild(evh);

	const evrows = document.createElement('div');
	evrows.className = 'evrows';
	rows.forEach((r) => {
		const row = document.createElement('div');
		row.className = 'evrow';


		const pnm = document.createElement('span');
		pnm.className = 'pnm';
		pnm.appendChild(document.createTextNode(r.Posten || ''));
		const small = document.createElement('small');
		small.textContent = r.Bereich || '';
		pnm.appendChild(small);
		row.appendChild(pnm);

		const tm = document.createElement('span');
		tm.className = 'tm';
		tm.textContent = `${kurzZeit(r.SchichtVon)}–${kurzZeit(r.SchichtBis)}`;
		row.appendChild(tm);

		const st = document.createElement('span');
		st.className = `st ${stateCls(r.filled || 0, r.BedarfAnzahl)}`;
		fuelleZaehler(st, r.filled || 0, r.BedarfAnzahl, r.geplant);
		row.appendChild(st);
		row.title = `${r.filled || 0}/${r.BedarfAnzahl} besetzt`;

		row.addEventListener('click', (e) => { e.stopPropagation(); onEvrowKlick(r._id); });
		evrows.appendChild(row);
	});
	bar.appendChild(evrows);
	return bar;
}

/**
 * Zeichnet das Event-Overlay über das Monatsgrid.
 * @param {object} opt
 * @param {HTMLElement} opt.month `.month`-Container (position:relative)
 * @param {array<object>} opt.events Events des sichtbaren Zeitraums
 * @param {Map<string, array>} opt.bedarfProEvent EventID -> Bedarfszeilen (mit filled)
 * @param {array<string>} opt.tageStrings alle Grid-Tage als YYYY-MM-DD (volle Wochen Mo–So)
 * @param {Map<string, HTMLElement>} opt.zellenNachTag YYYY-MM-DD -> Tageszelle (.mcell)
 * @param {function} opt.onEventKlick (event) => void
 * @param {function} opt.onEvrowKlick (bedarfID) => void
 * @param {function} opt.istEingeklappt (eventID) => boolean — Klappzustand beim Rendern
 * @param {function} opt.onKlappzustand (eventID, eingeklappt) => void — Zustand im Aufrufer merken
 */
function rendereEventOverlay({ month, events, bedarfProEvent, tageStrings, zellenNachTag, onEventKlick, onEvrowKlick, istEingeklappt, onKlappzustand }) {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
	positioniereAktuell = null;
	if (!month || !events || events.length === 0) {
		return;
	}
	klappKontext = { month, istEingeklappt, onKlappzustand };

	// Auf das Grid fallende Events samt ihrer abgedeckten Tag-Indizes.
	const sichtbar = [];
	events.forEach((ev) => {
		const von = dayjs(ev.DatumVon).startOf('day');
		const bis = dayjs(ev.DatumBis).startOf('day');
		const indizes = [];
		tageStrings.forEach((t, i) => {
			const d = dayjs(t);
			if (!d.isBefore(von, 'day') && !d.isAfter(bis, 'day')) indizes.push(i);
		});
		if (indizes.length) sichtbar.push({ ev, indizes });
	});
	if (!sichtbar.length) {
		return;
	}

	const laneById = berechneLanes(sichtbar.map((s) => s.ev));
	const laneH = [];
	const dayLaneCount = {};
	sichtbar.forEach(({ ev, indizes }) => {
		const lane = laneById.get(ev._id);
		// For multi-day events reserve lane heights and per-day spacers so bars can float above cells.
		// Single-day (expanded) cards will be inserted into the actual day cell, so they must not
		// contribute to the absolute lane height / spacer calculation (avoids large gaps).
		if (!istEintaegig(ev)) {
			// + LANE_ABSTAND zwischen gestapelten Lanes (der Abstand zum Zell-Inhalt wird über die negative
			// margin-bottom von .ev-spacer wieder eingezogen, siehe CSS).
			laneH[lane] = Math.max(laneH[lane] || 0, eventHoehe(ev, bedarfProEvent.get(ev._id) || []) + LANE_ABSTAND);
			indizes.forEach((i) => { dayLaneCount[i] = Math.max(dayLaneCount[i] || 0, lane + 1); });
		}
	});
	const laneTop = (lane) => laneH.slice(0, lane).reduce((a, b) => a + (b || 0), 0);

	// Spacer je abgedeckter Zelle reservieren, damit die Bedarf-Chips unter die Balken rutschen.
	Object.keys(dayLaneCount).forEach((i) => {
		const zelle = zellenNachTag.get(tageStrings[i]);
		const dn = zelle?.querySelector('.dn');
		if (!dn) return;
		const spacer = document.createElement('div');
		spacer.className = 'ev-spacer';
		// Unter dem letzten Balken zählt statt des Lane-Abstands der Abstand zum Zell-Inhalt
		// (abzgl. margin-bottom -4px + 5px Zell-Gap des Spacers).
		spacer.style.height = `${laneTop(dayLaneCount[i]) - LANE_ABSTAND + INHALT_ABSTAND - 1}px`;
		dn.after(spacer);
	});

	// Balken/Karten je Wochensegment erzeugen (mehrwöchige Events werden gesplittet).
	const segmente = [];
	sichtbar.forEach(({ ev, indizes }) => {
		const lane = laneById.get(ev._id);
		const rows = bedarfProEvent.get(ev._id) || [];
		const proWoche = new Map();
		indizes.forEach((i) => {
			const w = Math.floor(i / 7);
			if (!proWoche.has(w)) proWoche.set(w, []);
			proWoche.get(w).push(i);
		});
		const eingeklappt = istEingeklappt(ev._id);
		proWoche.forEach((grpIdx) => {
			const el = istEintaegig(ev)
				? baueExpandedCard(ev, rows, onEventKlick, onEvrowKlick, eingeklappt)
				: baueBar(ev, rows, onEventKlick, eingeklappt);

			if (istEintaegig(ev)) {
				// Eintägige Karten fließen in der Zelle mit: in den `.mcell-events`-Block (nach den In-Zelle-
				// Gruppen mehrtägiger Events, vor den normalen Bereich-Gruppen), nicht absolut positioniert.
				const cell = zellenNachTag.get(tageStrings[grpIdx[0]]);
				if (cell) {
					eventBlock(cell).appendChild(el);
					return;
				}
				// Fallback: absolut positionieren, falls die Zelle fehlt.
			}

			month.appendChild(el);
			segmente.push({ el, segFrom: tageStrings[Math.min(...grpIdx)], segTo: tageStrings[Math.max(...grpIdx)], lane });
		});
	});

	const positioniere = () => {
		const mRect = month.getBoundingClientRect();
		segmente.forEach(({ el, segFrom, segTo, lane }) => {
			const c1 = zellenNachTag.get(segFrom);
			const c2 = zellenNachTag.get(segTo);
			const dn = c1?.querySelector('.dn');
			if (!c1 || !c2 || !dn) return;
			const r1 = c1.getBoundingClientRect();
			const r2 = c2.getBoundingClientRect();
			const dnRect = dn.getBoundingClientRect();
			el.style.left = `${r1.left - mRect.left + 6}px`;
			el.style.width = `${r2.right - r1.left - 12}px`;
			el.style.top = `${dnRect.bottom - mRect.top + 5 + laneTop(lane)}px`;
		});
	};
	positioniere();
	positioniereAktuell = positioniere;
	verknuepfeHover(month);
	resizeObserver = new ResizeObserver(positioniere);
	resizeObserver.observe(month);
}

/**
 * Der Event-Block einer Tageszelle (`.mcell-events`), direkt unter Tageszeile + Balken-Spacer und vor den
 * normalen Bereich-Gruppen (`.mcell-bedarf`). Wird bei Bedarf angelegt.
 * @param {HTMLElement} zelle .mcell
 * @returns {HTMLElement}
 */
function eventBlock(zelle) {
	let block = zelle.querySelector('.mcell-events');
	if (!block) {
		block = document.createElement('div');
		block.className = 'mcell-events';
		// vor die Bereichsgruppen bzw. die Kosten-Fußzeile (die immer unten in der Zelle steht)
		const anker = zelle.querySelector('.mcell-bedarf') ?? zelle.querySelector('.ko-foot');
		if (anker) anker.before(block); else zelle.appendChild(block);
	}
	return block;
}

export { rendereEventOverlay, berechneLanes, eventBlock, setzeAlleEventsEingeklappt };
